/** Hex Totem (TOWERS_V2): a curse aura — enemies inside take more damage from everything and are healed less. */
import { isHex } from "../../data/balance";
import { Fix64 } from "../../maths/fix64";
import { distanceSquared } from "../../world/roadGeometry";
import { Enemy, Tower } from "../entities";
import { SimEventKind } from "../events";
import { LevelSim } from "../levelSim";
import { mythicValue } from "../mythics";
import { tierLine } from "../branches/ascension";
import { hexTakenAtLevel } from "../levelGrowth";

const PERCENT = 100;
const HALF = Fix64.parse("0.5");

/**
 * Once per wave tick, after movement and before anything deals damage: each enemy wears the strongest curse of the
 * working totems whose aura (their range) it is inside — never more than one (ties → the lower tower id) — or none.
 * EnemyHexed marks the tick an enemy comes under a curse or changes totem.
 */
export function tickHexes(sim: LevelSim): void {
  let totems = 0;
  for (const t of sim.towerSlots) if (t.alive && isHex(sim.balance.towers[t.archetype])) totems++;
  if (totems === 0 && !sim.anyHexed) return;
  sim.anyHexed = false;
  for (const e of sim.enemySlots) {
    if (!e.alive) continue;
    let best: Tower | null = null, bestBonus = Fix64.Zero;
    if (totems > 0 && !e.burrowed) {                      // a Burrower underground is out of the curse's reach (ENEMIES_V2)
      for (const t of sim.towerSlots) {
        if (!t.alive || t.disabledTicksLeft > 0 || !isHex(sim.balance.towers[t.archetype])) continue;
        const r = sim.rangeOf(t);
        if (distanceSquared(Fix64.fromInt(t.cell.x), Fix64.fromInt(t.cell.y), e.x, e.y).gt(r.mul(r))) continue;
        const bonus = hexBonusOf(sim, t);
        if (best === null || bonus.gt(bestBonus) || (bonus.eq(bestBonus) && t.id < best.id)) { best = t; bestBonus = bonus; }
      }
    }
    curse(sim, e, best, bestBonus);
  }
}

/**
 * The extra damage taken under this totem's curse: hexDamageTaken (grown by its type's level, sim/levelGrowth.ts) × its
 * tier's damageMult, plus Deep Curse. `tier`
 * (default its own) lets the Upgrade card preview the next tier's curse.
 */
export function hexBonusOf(sim: LevelSim, t: Tower, tier = t.tier): Fix64 {
  const a = sim.balance.towers[t.archetype];
  return hexTakenAtLevel(a, sim.towerLevels[t.archetype] ?? 1).mul(tierLine(sim, t.archetype, tier).damageMult).add(mythicValue(sim, t.archetype, sim.mythics.hexDeepCurse));
}

/** Healing on `target` from `healer` after the curse: cut once by the larger cut either of them wears. */
export function hexedHeal(healer: Enemy | null, target: Enemy, amount: Fix64): Fix64 {
  const cut = healer !== null && healer.hexHealCut.gt(target.hexHealCut) ? healer.hexHealCut : target.hexHealCut;
  return cut.isZero() ? amount : amount.mul(Fix64.One.sub(cut));
}

function curse(sim: LevelSim, e: Enemy, t: Tower | null, bonus: Fix64): void {
  if (t === null) { e.hexBonus = Fix64.Zero; e.hexHealCut = Fix64.Zero; e.hexTowerId = 0; return; }
  sim.anyHexed = true;
  const fresh = e.hexTowerId !== t.id;
  e.hexBonus = bonus;
  e.hexHealCut = sim.balance.towers[t.archetype].hexHealCut;
  e.hexTowerId = t.id;
  if (fresh) sim.events.add(SimEventKind.EnemyHexed, e.id, t.id, bonus.mulInt(PERCENT).add(HALF).floorToInt());
}
